import React, { useEffect, useState } from 'react';
import { EditIcon, XIcon, LoaderIcon } from 'lucide-react';
type RecurringEditScope = 'this' | 'future' | 'all';
interface RecurringEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scope: RecurringEditScope) => void;
  darkMode: boolean;
  lessonTitle: string;
  studentName: string;
  isUpdating: boolean;
  fetchCount: (scope: RecurringEditScope) => Promise<number>;
}
export function RecurringEditModal({
  isOpen,
  onClose,
  onConfirm,
  darkMode,
  lessonTitle,
  studentName,
  isUpdating,
  fetchCount
}: RecurringEditModalProps) {
  const [scope, setScope] = useState<RecurringEditScope>('this');
  const [count, setCount] = useState(1);
  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setScope('this');
      setCount(1);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleScopeChange = async (newScope: RecurringEditScope) => {
    setScope(newScope);
    if (newScope === 'this') {
      setCount(1);
    } else {
      const c = await fetchCount(newScope);
      setCount(c);
    }
  };
  const scopeOptions: {
    value: RecurringEditScope;
    label: string;
    description: string;
  }[] = [{
    value: 'this',
    label: 'This lesson only',
    description: 'Only this single occurrence will be updated'
  }, {
    value: 'future',
    label: 'This and all future lessons',
    description: 'This lesson and all upcoming occurrences in the series'
  }, {
    value: 'all',
    label: 'All lessons in the series',
    description: 'Every occurrence in this recurring series will be updated'
  }];
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-hidden" data-id="element-2202">
      <div className={`w-full max-w-md rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-navy-800 border border-navy-600' : 'bg-white border border-gray-200'}`} data-id="element-2203">
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-2204">
          <div className="flex items-center gap-3" data-id="element-2205">
            <div className="p-2 bg-blue-500/10 rounded-lg" data-id="element-2206">
              <EditIcon className="h-5 w-5 text-blue-500" data-id="element-2207" />
            </div>
            <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2208">
              Edit Recurring Lesson
            </h3>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-navy-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`} data-id="element-2209">
            <XIcon className="h-5 w-5" data-id="element-2210" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4" data-id="element-2211">
          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2212">
            This lesson is part of a recurring series. What would you like to
            update?
          </p>

          {/* Lesson info */}
          <div className={`p-3 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-2213">
            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2214">
              {lessonTitle}
            </p>
            <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2215">
              {studentName}
            </p>
          </div>

          {/* Radio options */}
          <div className="space-y-3" data-id="element-2216">
            {scopeOptions.map(option => <label key={option.value} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${scope === option.value ? darkMode ? 'border-blue-500/50 bg-blue-500/5' : 'border-blue-400 bg-blue-50' : darkMode ? 'border-navy-600 hover:border-navy-500' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2217">
                <input type="radio" name="recurringEditScope" value={option.value} checked={scope === option.value} onChange={() => handleScopeChange(option.value)} className="mt-0.5 accent-blue-500" data-id="element-2218" />
                <div data-id="element-2219">
                  <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2220">
                    {option.label}
                  </p>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2221">
                    {option.description}
                  </p>
                </div>
              </label>)}
          </div>
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-3 p-4 border-t ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-2222">
          <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`} data-id="element-2223">
            Cancel
          </button>
          <button onClick={() => onConfirm(scope)} disabled={isUpdating} className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center" data-id="element-2224">
            {isUpdating ? <>
                <LoaderIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-2225" />
                Updating...
              </> : `Update ${count} lesson${count !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>;
}